import { differenceInSeconds, parseISO } from "date-fns";
import { Identifier } from "ra-core";
import React, { Fragment } from "react";
import { useSelector } from "react-redux";
import { EventViewDto } from "../../../../common/data/service-proxies";
import { AppState } from "../../../../common/types";
import { getEventGraphHeight, getPreviousDutyStatus } from "./graphUtils";
import classnames from "classnames";

interface Props {
  hover?: boolean;
  id: Identifier;
  index: number;
  startTime: string;
  endTime: string;
  dutyStatus?: string | null;
  lineColor: string;
  basicEvents: Array<EventViewDto & { lineColor: string }>;
  startOfDay?: string;
  onClick?: React.MouseEventHandler<SVGRectElement>;
  onMouseEnter?: React.MouseEventHandler<SVGRectElement>;
  onMouseLeave?: React.MouseEventHandler<SVGRectElement>;
  noHighlight?: boolean;
  offsetX?: number;
  offsetY?: number;
}

export const HosEventLine = ({
  hover,
  id,
  index,
  startTime,
  endTime,
  dutyStatus,
  lineColor,
  basicEvents,
  startOfDay,
  onClick,
  onMouseEnter,
  onMouseLeave,
  noHighlight,
  offsetX = 0,
  offsetY = 0,
}: Props) => {
  const themeMode = useSelector((x: AppState) => x.theme);
  if (!startOfDay) {
    return null;
  }

  const startSeconds = differenceInSeconds(
    parseISO(startTime!),
    parseISO(startOfDay)
  );
  const endSeconds = differenceInSeconds(
    parseISO(endTime!),
    parseISO(startOfDay)
  );

  let previousY: number | null = null;
  const prevDutyStatus = getPreviousDutyStatus(basicEvents, index);
  if (prevDutyStatus) {
    previousY = getEventGraphHeight(prevDutyStatus);
  }

  if (previousY) {
    previousY += offsetY;
  }

  const startingPoint = offsetX + (50 * startSeconds) / 3600;
  const endingPoint = offsetX + (50 * endSeconds) / 3600;
  let dashArray: string | undefined;
  let y = getEventGraphHeight(dutyStatus!);
  if (y) {
    y += offsetY;
  }

  if (
    dutyStatus === "PC" ||
    dutyStatus === "YM" ||
    dutyStatus === "PC-E" ||
    dutyStatus === "YM-E"
  ) {
    dashArray = "1,2";
  }

  return y ? (
    <Fragment>
      <line
        className="hos-event"
        x1={startingPoint}
        x2={endingPoint}
        y1={y}
        y2={y}
        style={{
          stroke: lineColor,
          strokeDasharray: dashArray,
        }}
      />
      {!noHighlight ? (
        <rect
          className={
            hover
              ? classnames(
                  "hos-event-highlight",
                  `hos-event-highlight-hovered-${themeMode}`
                )
              : "hos-event-highlight"
          }
          x={startingPoint}
          y={50 + offsetY}
          height={160}
          width={Math.abs(endingPoint - startingPoint)}
          style={{ fill: lineColor }}
          onClick={onClick}
          onMouseLeave={onMouseLeave}
          onMouseEnter={onMouseEnter}
        />
      ) : null}
      {previousY && (
        <line
          className="hos-event-transition"
          x1={startingPoint}
          x2={startingPoint}
          y1={previousY > y ? previousY + 2 : previousY - 2}
          y2={previousY > y ? y - 2 : y + 2}
          style={{
            stroke: lineColor,
          }}
        />
      )}
    </Fragment>
  ) : null;
};
